/**
 * useCalcHandlers — extraído de ForecastPage.tsx (P7 split).
 *
 * Consolida handlers relacionados con cálculo de forecast + bulk actions:
 *   - runForecastCalc, openCalcDialog, runPreselection, handleDialogMonthsChange.
 *   - applyBulkActiveForPurchase, handleConfirmAction, handleConfirmDialogConfirm.
 */

import {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useCallback,
} from "react";
import { ProductData } from "./types";
import {
  type CalcResult,
  type SamplesByProduct,
} from "../../components/Forecast/SelectedProductsDialog";
import { runForecastCalc as runForecastCalcImpl } from "./run-forecast-calc";
import { runPreselection as runPreselectionImpl } from "./page-handlers";
import { applyBulkActiveForPurchase as applyBulkActiveForPurchaseImpl } from "./apply-bulk-active";

interface UseCalcHandlersOptions {
  selectedIds: Set<string>;
  products: ProductData[];
  filteredAndSortedProducts: ProductData[];
  parentGlobalMarginRatio: number | null;
  forecastMonths: number;
  selectedSupplierEstimatedAmount: number | null;
  confirmAction: "activate" | "deactivate" | null;
  setProducts: Dispatch<SetStateAction<ProductData[]>>;
  setSelectedIds: Dispatch<SetStateAction<Set<string>>>;
  setParentCalcResults: Dispatch<SetStateAction<CalcResult[]>>;
  setParentSamplesByProduct: Dispatch<SetStateAction<SamplesByProduct>>;
  setParentCalcLoading: Dispatch<SetStateAction<boolean>>;
  setParentGlobalMarginRatio: Dispatch<SetStateAction<number | null>>;
  setForecastMonths: Dispatch<SetStateAction<number>>;
  setDialogBaseSelectedIds: Dispatch<SetStateAction<Set<string>>>;
  setDialogBaseEstimatedAmount: Dispatch<SetStateAction<number | null>>;
  setHorizonAddedIds: Dispatch<SetStateAction<Set<string>>>;
  setCalcDialogOpen: Dispatch<SetStateAction<boolean>>;
  setConfirmAction: Dispatch<
    SetStateAction<"activate" | "deactivate" | null>
  >;
  setConfirmDialogOpen: Dispatch<SetStateAction<boolean>>;
  restoreSelectionOnDialogCloseRef: MutableRefObject<boolean>;
  closeActionsMenu: () => void;
  closeConfirmDialog: () => void;
}

export function useCalcHandlers(opts: UseCalcHandlersOptions): {
  runForecastCalc: (m: number, ids?: Set<string>) => Promise<void>;
  openCalcDialog: () => void;
  runPreselection: (m: number) => Promise<Set<string>>;
  handleDialogMonthsChange: (m: number) => void;
  applyBulkActiveForPurchase: (mode: "on" | "off") => Promise<void>;
  handleConfirmAction: (action: "activate" | "deactivate") => void;
  handleConfirmDialogConfirm: () => void;
} {
  const {
    selectedIds,
    products,
    filteredAndSortedProducts,
    parentGlobalMarginRatio,
    forecastMonths,
    selectedSupplierEstimatedAmount,
    confirmAction,
    setProducts,
    setSelectedIds,
    setParentCalcResults,
    setParentSamplesByProduct,
    setParentCalcLoading,
    setParentGlobalMarginRatio,
    setForecastMonths,
    setDialogBaseSelectedIds,
    setDialogBaseEstimatedAmount,
    setHorizonAddedIds,
    setCalcDialogOpen,
    setConfirmAction,
    setConfirmDialogOpen,
    restoreSelectionOnDialogCloseRef,
    closeActionsMenu,
    closeConfirmDialog,
  } = opts;

  const runForecastCalc = useCallback(
    (months: number, overrideIds?: Set<string>) =>
      runForecastCalcImpl({
        months,
        overrideIds,
        selectedIds,
        products,
        parentGlobalMarginRatio,
        setParentCalcResults,
        setParentSamplesByProduct,
        setParentCalcLoading,
        setSelectedIds,
        setParentGlobalMarginRatio,
      }),
    [products, selectedIds, parentGlobalMarginRatio],
  );

  const openCalcDialog = useCallback(() => {
    setDialogBaseSelectedIds(new Set(selectedIds));
    setDialogBaseEstimatedAmount(selectedSupplierEstimatedAmount);
    setHorizonAddedIds(new Set());
    restoreSelectionOnDialogCloseRef.current = true;
    setCalcDialogOpen(true);
    void runForecastCalc(forecastMonths);
  }, [
    selectedIds,
    selectedSupplierEstimatedAmount,
    runForecastCalc,
    forecastMonths,
  ]);

  const runPreselection = useCallback(
    (months: number) =>
      runPreselectionImpl({
        months,
        filteredAndSortedProducts,
        setSelectedIds,
      }),
    [filteredAndSortedProducts],
  );

  const handleDialogMonthsChange = useCallback(
    (months: number) => {
      setForecastMonths(months);
      void runForecastCalc(months);
    },
    [runForecastCalc],
  );

  const applyBulkActiveForPurchase = useCallback(
    (mode: "on" | "off") =>
      applyBulkActiveForPurchaseImpl({
        mode,
        selectedIds,
        setProducts,
        setSelectedIds,
        closeActionsMenu,
      }),
    [selectedIds, closeActionsMenu],
  );

  const handleConfirmAction = useCallback(
    (action: "activate" | "deactivate") => {
      setConfirmAction(action);
      setConfirmDialogOpen(true);
      closeActionsMenu();
    },
    [closeActionsMenu],
  );

  const handleConfirmDialogConfirm = useCallback(() => {
    if (confirmAction === "activate") {
      applyBulkActiveForPurchase("on");
    } else if (confirmAction === "deactivate") {
      applyBulkActiveForPurchase("off");
    }
    closeConfirmDialog();
  }, [confirmAction, applyBulkActiveForPurchase, closeConfirmDialog]);

  return {
    runForecastCalc,
    openCalcDialog,
    runPreselection,
    handleDialogMonthsChange,
    applyBulkActiveForPurchase,
    handleConfirmAction,
    handleConfirmDialogConfirm,
  };
}
